import { Copy, ExternalLink, FileDiff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { JsonTable } from "~/components/json-table";
import { SectionCard } from "~/components/section-card";
import { Badge, Button, Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui";
import type { ChangeRequestItemUI } from "~/features/changeRequests/types";
import { cn } from "~/lib/utils";
import { ChangeRequestItemDiff } from "./ChangeRequestItemDiff";

interface ChangeRequestItemSectionProps {
  item: ChangeRequestItemUI;
}

export function ChangeRequestItemSection({ item }: ChangeRequestItemSectionProps) {
  const { t } = useTranslation();

  const copy = async (value: string) => {
    try {
      await copyToClipboard(value);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  const recordPath =
    item.recordLink && item.recordId
      ? `/${item.recordLink === "entity" ? "entities" : "relations"}/${item.recordId}`
      : null;
  const idValue = item.recordId ?? item.recordMetadataId;
  const idLabel = item.recordMetadataId
    ? t("model.common.xID", { x: t("model.recordMetadata.modelName") })
    : t("model.common.id");

  return (
    <SectionCard icon={FileDiff} title={t("feature.changeRequests.detail.itemTitle", { type: item.modelTypeLabel })}>
      <div className="space-y-4 text-[13px]">
        <div className="grid grid-cols-[minmax(7rem,10rem)_1fr] items-start gap-x-4 gap-y-2">
          <span className="text-fg-3">{t("model.changeRequest.operationType")}</span>
          <span className="capitalize text-fg-1">{item.operationType}</span>

          {idValue && (
            <>
              <span className="text-fg-3">{idLabel}</span>
              <span className="flex items-center gap-1.5">
                <span className="break-all tabular-nums text-fg-1">{idValue}</span>
                <CopyButton onCopy={() => copy(idValue)} label={t("ui.copyId")} />
                {recordPath && (
                  <Link to={recordPath} className="text-fg-link hover:underline" aria-label={t("ui.view")}>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                )}
              </span>
            </>
          )}

          {item.categoryPresent && (
            <>
              <span className="flex items-center gap-2 text-fg-3">
                {t("model.common.xIDs", { x: t("model.category.modelName") })}
                {item.categoryChanged && <Badge tone="score-b">{t("feature.changeRequests.detail.changed")}</Badge>}
              </span>
              <div className="flex flex-col gap-1">
                {item.categoryChanged && item.originalCategoryIds.length > 0 && (
                  <CategoryIds ids={item.originalCategoryIds} onCopy={copy} copyLabel={t("ui.copyId")} tone="old" />
                )}
                <CategoryIds
                  ids={item.categoryIds}
                  onCopy={copy}
                  copyLabel={t("ui.copyId")}
                  tone={item.categoryChanged ? "new" : "plain"}
                />
              </div>
            </>
          )}

          <span className="text-fg-3">{t("model.common.deactivatedAt")}</span>
          <span className="text-fg-1">
            {item.deactivatedAt ? t("date.long", { date: item.deactivatedAt }) : "null"}
          </span>
        </div>

        <div>
          <h3 className="mb-2 text-12-bold capitalize text-fg-2">{t("model.common.properties")}</h3>
          <Tabs defaultValue="json">
            <TabsList>
              <TabsTrigger value="json">{t("ui.json")}</TabsTrigger>
              <TabsTrigger value="diff">{t("ui.diff")}</TabsTrigger>
            </TabsList>
            <TabsContent value="json" className="pt-3">
              <JsonTable target={item.modelProps} />
            </TabsContent>
            <TabsContent value="diff" className="pt-3">
              <ChangeRequestItemDiff oldValue={item.diffOld} newValue={item.diffNew} viewMode="detail" />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </SectionCard>
  );
}

function CopyButton({ onCopy, label }: { onCopy: () => void; label: string }) {
  return (
    <Button type="button" variant="ghost" size="icon" className="h-5 w-5 shrink-0" aria-label={label} onClick={onCopy}>
      <Copy className="h-3 w-3" />
    </Button>
  );
}

function CategoryIds({
  ids,
  onCopy,
  copyLabel,
  tone,
}: {
  ids: string[];
  onCopy: (value: string) => void;
  copyLabel: string;
  tone: "old" | "new" | "plain";
}) {
  const { t } = useTranslation();
  if (ids.length === 0) return <span className="text-fg-3">—</span>;
  const color = tone === "old" ? "text-score-c" : tone === "new" ? "text-score-a" : "text-fg-1";
  const prefix = tone === "old" ? "− " : tone === "new" ? "+ " : "";
  return (
    <div className="flex flex-col gap-0.5">
      {ids.map((id) => (
        <span key={id} className={cn("flex items-center gap-1.5", color)}>
          <span className="break-all tabular-nums">
            {prefix}
            {id}
          </span>
          <CopyButton onCopy={() => onCopy(id)} label={copyLabel} />
          <Link
            to={`/admin/categories/${id}`}
            className="shrink-0 text-fg-link hover:text-primary-700"
            aria-label={t("ui.view")}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </span>
      ))}
    </div>
  );
}
